import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/images/history/scene_00.png"
          alt="The imperial court — an emperor enthroned in an embroidered kaba"
          className="h-full w-full object-cover object-[center_30%] opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/35 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-ink/40" />
      </motion.div>

      <div className="relative z-10 flex h-full flex-col justify-end px-5 pb-24 sm:px-10 lg:px-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-gold"
          data-testid="hero-eyebrow"
        >
          HILMAY&rsquo;S KABAS · THE HISTORY OF THE KABA
        </motion.p>
        <h1
          data-testid="hero-headline"
          className="font-display text-4xl leading-[1.04] tracking-tight text-cream sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.35}>A THOUSAND YEARS</MaskedLine>
          <MaskedLine delay={0.5}>IN VELVET</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="italic text-gold">&amp; GOLD.</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-6 max-w-md text-base font-light leading-relaxed text-stone2 sm:text-lg"
        >
          The story of the Habesha kaba — the velvet and gold ceremonial cape of
          Ethiopia and Eritrea, worn by emperors, warriors, priests and brides.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-4 sm:flex"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted2">Scroll</span>
        <span className="scroll-cue-line block h-14 w-px bg-gold/60" />
      </motion.div>
    </section>
  );
}
